import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import FoodDetailModal from './FoodDetailModal';
import ResidenceSelector from './ResidenceSelector';
import { getAllFoodItems, getFoodImageUrl } from '../services/foodItemService';
import { getFoodImages } from '../services/foodImageService';
import { getPickupPointById } from '../services/pickupPointService';
import { getProfileByUserId } from '../services/profileService';
import { getAverageRatingByVendorId } from '../services/reviewService';
import { getAllResidences } from '../services/residenceService';
import { addToCart } from '../services/cartService';
import './LandingPage.css';

// Import backdrop image
import HeroBackdrop from './CondoEats Homepage Backdrop.png';

// Hero Section Component
function HeroSection() {
  const { profile, isAuthenticated } = useAuth();

  // Determine which buttons to show based on user role
  const showBuyFood = !isAuthenticated || !profile || profile.role === 'customer' || profile.role === 'vendor';
  const showSellFood = !isAuthenticated || !profile || profile.role === 'vendor' || profile.role === 'customer';

  return (
    <section className="hero-section">
      <div className="hero-backdrop">
        <img src={HeroBackdrop} alt="" className="hero-backdrop-image" />
      </div>
      <div className="hero-content">
        <h1 className="hero-title">
          Fresh<br />
          Homemade Food<br />
          From Your Own<br />
          Residence
        </h1>
        <p className="hero-subtitle">
          Pre-order from neighbours. No delivery. Pick up at the lobby. Always fresh.
        </p>
        <div className="hero-buttons">
          {showBuyFood && (
            <Link to="/buy-food" className="hero-btn hero-btn-primary">
              BUY FOOD
            </Link>
          )}
          {showSellFood && (
            <Link to="/sell-food" className="hero-btn hero-btn-secondary">
              SELL FOOD
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// Food Card Component
function FoodCard({
  image,
  images,
  mealType,
  rating,
  title,
  price,
  preparedBy,
  orderBy,
  pickupTime,
  pickupLocation,
  ordersLeft,
  isVegetarian,
  availability,
  vendorId,
  id,
  onClick,
  onQuickAddToCart,
  addingToCart,
  user,
  cutoffTime
}) {
  const isSoldOut = ordersLeft === 0;
  const isExpired = cutoffTime && new Date(cutoffTime) < new Date();
  const isUnavailable = isSoldOut || isExpired;

  return (
    <div
      className={`food-card ${isUnavailable ? 'unavailable' : ''}`}
      onClick={onClick}
      style={{ cursor: isUnavailable ? 'default' : 'pointer' }}
    >
      <div className="food-card-image-container">
        {image ? (
          <img src={image} alt={title} className="food-card-image" />
        ) : (
          <div className="food-card-image-placeholder">
            <span>🍽️</span>
          </div>
        )}
        <span className={`food-availability-badge ${availability}`}>
          {availability === 'available' ? 'Available now' : 'Pre-order'}
        </span>
        {isExpired && isSoldOut && (
          <div className="status-overlay unavailable">
            Unavailable
          </div>
        )}
        {isExpired && !isSoldOut && (
          <div className="status-overlay expired">
            Ordering Period Over
          </div>
        )}
        {isSoldOut && !isExpired && (
          <div className="status-overlay sold-out">
            Sold Out
          </div>
        )}
      </div>

      <div className="food-card-content">
        <div className="food-card-header">
          <span className="food-meal-type">{mealType}</span>
          <div className="food-rating">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
            </svg>
            <span>{rating || rating === 0 ? rating.toFixed(1) : 'No Rating Yet'}</span>
          </div>
        </div>

        <div className="food-name-container">
          <h3 className="food-title">{title}</h3>
          {isVegetarian && (
            <span className="food-vegetarian-icon" title="Vegetarian">🌱</span>
          )}
        </div>

        <p className="food-price">RM {price}</p>

        {isVegetarian && (
          <div className="food-vegetarian-badge" title="Vegetarian">
            <span className="food-veg-icon" aria-hidden="true">🟢</span>
            <span className="food-veg-text" title="Vegetarian">Vegetarian</span>
          </div>
        )}

        <div className="food-details">
          <p className="food-vendor">Prepared by {preparedBy}</p>
          <p className="food-order-time">Order by: {orderBy}</p>
          <p className="food-pickup">Pickup: {pickupTime}</p>
        </div>

        <p className="food-orders-left">Orders left: {ordersLeft}</p>

        <button
          className="food-add-to-cart-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (onQuickAddToCart) {
              onQuickAddToCart(e, { id, name: title, price, vendorId, ordersLeft });
            }
          }}
          disabled={addingToCart === id || isUnavailable || (user && user.$id === vendorId)}
          title={user && user.$id === vendorId ? 'You cannot add your own item to cart' : undefined}
        >
          {addingToCart === id ? (
            <>
              <span className="spinner-small"></span> Adding...
            </>
          ) : (isSoldOut && isExpired) ? (
            'Unavailable'
          ) : isSoldOut ? (
            'Sold Out'
          ) : isExpired ? (
            'Ordering Period Over'
          ) : user && user.$id === vendorId ? (
            'Your Item'
          ) : (
            <>
              <span>🛒</span> Add to Cart
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Food Preview Section Component
function FoodPreviewSection() {
  const navigate = useNavigate();
  const { user, refreshCart } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const [addingToCart, setAddingToCart] = useState(null);

  const [selectedResidence, setSelectedResidence] = useState('Select your residence');
  const [selectedResidenceId, setSelectedResidenceId] = useState(null);
  const [residences, setResidences] = useState([]);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [slideDirection, setSlideDirection] = useState('next');

  // Modal state
  const [selectedFood, setSelectedFood] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle responsive items per page
  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth <= 767) {
        // Mobile: 1 item
        setItemsPerPage(1);
      } else if (window.innerWidth < 1200) {
        // Tablet and iPad Pro: 2 items
        setItemsPerPage(2);
      } else {
        // Desktop: 3 items
        setItemsPerPage(3);
      }
    };

    // Set initial value
    updateItemsPerPage();

    // Add event listener
    window.addEventListener('resize', updateItemsPerPage);

    // Cleanup
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  // Reset carousel position when itemsPerPage changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [itemsPerPage]);

  // Fetch residences on mount to map names -> ids
  useEffect(() => {
    const fetchResidences = async () => {
      try {
        const condos = await getAllResidences();
        setResidences(condos);

        if (condos && condos.length > 0) {
          // Default to first residence if no selection yet
          const defaultCondo = condos[0];
          if (selectedResidence === 'Select your residence') {
            setSelectedResidence(defaultCondo.name);
            setSelectedResidenceId(defaultCondo.id);
          } else {
            const found = condos.find(c => c.name === selectedResidence);
            if (found) setSelectedResidenceId(found.id);
          }
        }
      } catch (err) {
        console.error('Error fetching residences:', err);
      }
    };

    fetchResidences();
  }, []);

  // Fetch food listings from database when residence changes
  useEffect(() => {
    const fetchFoodListings = async () => {
      try {
        setLoading(true);
        setError(null);

        const foodItems = await getAllFoodItems();

        // Transform database food items to display format (no more manual cutoff filtering)
        const transformed = await Promise.all(
          foodItems.map(async (item) => {
            // Images
            let imageUrls = [];
            try {
              const imgs = await getFoodImages(item.$id);
              imageUrls = imgs.map(img => getFoodImageUrl(img.imageId));
            } catch (imgErr) {
              console.error('Error getting images for food item:', imgErr);
            }
            const primaryImage = imageUrls.length > 0 ? imageUrls[0] : null;

            // Pickup point
            let pickupLocation = 'Pickup location';
            let condoId = null;
            try {
              if (item.pickup_id) {
                const pickupPoint = await getPickupPointById(item.pickup_id);
                pickupLocation = pickupPoint.name;
                condoId = pickupPoint.condoId;
              }
            } catch (pickupErr) {
              console.error('Error getting pickup point:', pickupErr);
            }

            // Vendor
            let vendorName = 'Vendor';
            try {
              if (item.vendor_id) {
                const vendorProfile = await getProfileByUserId(item.vendor_id);
                vendorName = vendorProfile.vendor_name || vendorProfile.full_name || 'Vendor';
              }
            } catch (vendorErr) {
              console.error('Error getting vendor profile:', vendorErr);
            }

            // Rating
            let avgRating = null;
            try {
              if (item.vendor_id) {
                const ar = await getAverageRatingByVendorId(item.vendor_id);
                avgRating = ar;
              }
            } catch (ratingErr) {
              console.error('Error fetching rating:', ratingErr);
            }

            // Format meal type for display and filtering - handle both string and array formats
            let mealTypes = [];
            if (Array.isArray(item.meal_type)) {
              mealTypes = item.meal_type.map(t => t.toLowerCase());
            } else if (typeof item.meal_type === 'string') {
              mealTypes = item.meal_type.toLowerCase().split(',').map(t => t.trim());
            }

            // Display formatted version (Capitalized and joined)
            const displayMealType = mealTypes
              .map(t => t.charAt(0).toUpperCase() + t.slice(1))
              .join(', ') || 'Lunch';
            const primaryMealType = mealTypes[0] || 'lunch';

            // Format cutoff time (date + time) - same as BuyFoodPage
            const cutoffDate = new Date(item.cutoff_time);
            const orderByTime = cutoffDate && !isNaN(cutoffDate.getTime()) ? (
              cutoffDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) + ', ' + cutoffDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
            ) : 'Not specified';

            // Format pickup time (date + time) - same as BuyFoodPage
            const pickupDate = new Date(item.pickup_time);
            const pickupTime = pickupDate && !isNaN(pickupDate.getTime()) ? (
              pickupDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) + ', ' + pickupDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
            ) : 'Not specified';

            return {
              id: item.$id,
              image: primaryImage,
              mealType: displayMealType,
              rating: avgRating,
              title: item.item_name,
              price: item.price,
              preparedBy: vendorName,
              orderBy: orderByTime,
              pickupTime: pickupTime,
              pickupLocation: pickupLocation,
              ordersLeft: item.stock_count,
              condoId,
              images: imageUrls,
              vendorId: item.vendor_id,
              description: item.description,
              ingredients: item.ingredients ? item.ingredients.split(',').map(i => i.trim()) : [],
              isVegetarian: Array.isArray(item.dietary_label)
                ? item.dietary_label.some(l => l.toLowerCase() === 'vegetarian')
                : (item.dietary_label?.toLowerCase().includes('vegetarian') || false),
              availability: item.is_preorder ? 'preorder' : 'available',
              cutoffTime: item.cutoff_time
            };
          })
        );

        // Filter by selected residence if set
        let filtered = selectedResidenceId
          ? transformed.filter(t => t.condoId === selectedResidenceId)
          : transformed;

        // Sort: Prioritize available items (those not sold out and not past cutoff)
        const nowSort = new Date();
        filtered.sort((a, b) => {
          const aAvailable = a.ordersLeft > 0 && (!a.cutoffTime || new Date(a.cutoffTime) > nowSort);
          const bAvailable = b.ordersLeft > 0 && (!b.cutoffTime || new Date(b.cutoffTime) > nowSort);

          if (aAvailable && !bAvailable) return -1;
          if (!aAvailable && bAvailable) return 1;
          return 0;
        });

        // Limit to maximum 6 items for carousel
        setFoods(filtered.slice(0, 6));
        setCurrentIndex(0); // Reset carousel when residence changes
      } catch (err) {
        console.error('Error fetching food listings:', err);
        setError('Failed to load food items');
      } finally {
        setLoading(false);
      }
    };

    fetchFoodListings();
  }, [selectedResidenceId]);

  const handleResidenceSelect = (residence) => {
    setSelectedResidence(residence);
    const found = residences.find(r => r.name === residence);
    if (found) {
      setSelectedResidenceId(found.id);
      console.log('Selected residence:', residence, 'ID:', found.id);
    } else {
      setSelectedResidenceId(null);
    }
  };

  // Modal handlers
  const handleFoodClick = (food) => {
    const foodDetail = {
      id: food.id,
      name: food.title || food.name,
      price: food.price,
      image: food.image,
      images: food.images,
      category: food.mealType,
      rating: food.rating,
      vendorName: food.preparedBy,
      // Ensure vendorId is included so the modal's "Prepared by" link works correctly
      vendorId: food.vendorId,
      orderBy: food.orderBy,
      pickupTime: food.pickupTime,
      pickupLocation: food.pickupLocation,
      description: food.description || 'Delicious homemade meal prepared with care.',
      ingredients: food.ingredients || [],
      notice: 'Food will be delivered to the pickup location as specified.',
      ordersLeft: food.ordersLeft,
      isVegetarian: food.isVegetarian,
      availability: food.availability
    };
    setSelectedFood(foodDetail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFood(null);
  };

  const handleQuickAddToCart = async (e, food) => {
    e.stopPropagation();

    if (!user) {
      showInfo('Please log in to add items to cart');
      navigate('/login');
      return;
    }

    if (user && user.$id === food.vendorId) {
      showError('You cannot add your own food items to the cart.');
      return;
    }

    try {
      setAddingToCart(food.id);

      await addToCart(user.$id, food.id, 1);

      // Refresh TopBar cart
      if (refreshCart) refreshCart();

      showSuccess(`${food.name} added to cart! ✓`);
    } catch (error) {
      console.error('Error adding to cart:', error);

      let errorMessage = 'Failed to add item to cart. ';
      if (error.message.includes('not configured')) {
        errorMessage += 'Configuration error: ' + error.message;
      } else if (error.code === 404) {
        errorMessage += 'Cart collection not found. Please contact support.';
      } else if (error.code === 401) {
        errorMessage += 'You are not authorized. Please log in again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }

      showError(errorMessage);
      console.error('Full error details:', error);
    } finally {
      setAddingToCart(null);
    }
  };

  const handleAddToCart = async (cartItem) => {
    console.log('Adding to cart from modal:', cartItem);

    if (!user) {
      showInfo('Please log in to add items to cart');
      navigate('/login');
      return;
    }

    if (cartItem && cartItem.vendorId && user && user.$id === cartItem.vendorId) {
      showError('You cannot add your own food items to the cart.');
      return;
    }

    try {
      await addToCart(
        user.$id,
        cartItem.foodId,
        cartItem.quantity || 1
      );

      if (refreshCart) refreshCart();

      showSuccess(`${cartItem.foodName} added to cart! ✓`);
    } catch (error) {
      console.error('Error adding to cart:', error);

      let errorMessage = 'Failed to add item to cart. ';
      if (error.message.includes('not configured')) {
        errorMessage += 'Configuration error: ' + error.message;
      } else if (error.code === 404) {
        errorMessage += 'Cart collection not found. Please contact support.';
      } else if (error.code === 401) {
        errorMessage += 'You are not authorized. Please log in again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }

      showError(errorMessage);
      console.error('Full error details:', error);
    }
  };

  // Carousel navigation
  const handlePrevious = () => {
    setSlideDirection('prev');
    setCurrentIndex(prev => Math.max(0, prev - itemsPerPage));
  };

  const handleNext = () => {
    setSlideDirection('next');
    setCurrentIndex(prev => {
      // Move to the next set, but don't go beyond the last item
      const nextIndex = prev + itemsPerPage;
      // If next position would show empty spaces, jump to show last items
      if (nextIndex >= foods.length) {
        return Math.max(0, foods.length - itemsPerPage);
      }
      return nextIndex;
    });
  };

  // Calculate which items to display - create array based on itemsPerPage (1 for mobile, 2 for tablet, 3 for desktop)
  const visibleFoods = [];
  for (let i = 0; i < itemsPerPage; i++) {
    const foodIndex = currentIndex + i;
    visibleFoods.push(foodIndex < foods.length ? foods[foodIndex] : null);
  }

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex + itemsPerPage < foods.length;

  return (
    <section className="food-preview-section">
      <ResidenceSelector
        selectedResidence={selectedResidence}
        onResidenceSelect={handleResidenceSelect}
      />

      <div className="food-cards-container">
        {/* Left Arrow */}
        <button
          className={`food-swipe-arrow food-swipe-arrow-left ${!canGoPrev ? 'disabled' : ''}`}
          onClick={handlePrevious}
          disabled={!canGoPrev}
          aria-label="Previous food items"
        >
          <svg width="24" height="48" viewBox="0 0 24 48" fill="none">
            <path d="M20 4L4 24L20 44" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="food-cards-content">
          <div className={`food-cards-wrapper ${slideDirection === 'prev' ? 'slide-prev' : 'slide-next'}`} key={currentIndex}>
            {loading ? (
              <div className="loading-placeholder">Loading food items...</div>
            ) : error ? (
              <div className="error-placeholder">{error}</div>
            ) : foods.length === 0 ? (
              <div className="no-foods-placeholder">No food listings available.</div>
            ) : (
              visibleFoods.map((food, index) => (
                food ? (
                  <FoodCard
                    key={food.id}
                    {...food}
                    onClick={() => handleFoodClick(food)}
                    onQuickAddToCart={handleQuickAddToCart}
                    addingToCart={addingToCart}
                    user={user}
                  />
                ) : (
                  <div key={`empty-${index}`} className="food-card-empty-slot"></div>
                )
              ))
            )}
          </div>
          <Link to="/buy-food" className="see-all-btn">
            See all
          </Link>
        </div>

        {/* Right Arrow */}
        <button
          className={`food-swipe-arrow food-swipe-arrow-right ${!canGoNext ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label="Next food items"
        >
          <svg width="24" height="48" viewBox="0 0 24 48" fill="none">
            <path d="M4 4L20 24L4 44" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Food Detail Modal */}
      <FoodDetailModal
        food={selectedFood}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
        isOwner={user && selectedFood && user.$id === selectedFood.vendorId}
        onUpdate={(updated) => { setSelectedFood(updated); setFoods(prev => prev.map(f => f.id === updated.id ? { ...f, ingredients: updated.ingredients } : f)); }}
      />
    </section>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useToast();
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Handle topup success/cancel/failed redirects
  useEffect(() => {
    const topupStatus = searchParams.get('topup');

    if (topupStatus === 'success') {
      // Clear the query parameter
      searchParams.delete('topup');
      setSearchParams(searchParams, { replace: true });

      // Refresh the user's profile to get updated balance
      if (user && refreshProfile) {
        refreshProfile();
      }

      // Show success message
      showSuccess('Payment successful! Your balance has been updated.');
    } else if (topupStatus === 'cancel') {
      // Clear the query parameter
      searchParams.delete('topup');
      setSearchParams(searchParams, { replace: true });

      // Show info message
      showInfo('Payment was cancelled.');
    } else if (topupStatus === 'failed') {
      // Clear the query parameter
      searchParams.delete('topup');
      setSearchParams(searchParams, { replace: true });

      // Show error message
      showError('Payment failed. Please check your card details and try again.');
    }
  }, [searchParams, setSearchParams, showSuccess, showError, showInfo, user, refreshProfile]);

  return (
    <div className="landing-page">
      <TopBar />
      <main className="landing-main">
        <HeroSection />
        <FoodPreviewSection />
      </main>
    </div>
  );
}
